import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Zap } from "lucide-react";

/**
 * MarqueeBand — a giant slow-scrolling display marquee that acts as a
 * visual divider between homepage sections. Two rows moving in opposite
 * directions with big outlined display text — cinematic and punchy.
 */

const WORDS_TOP = [
  "OWN YOUR MASTERS",
  "STREAM FREE",
  "SELL DIRECT",
  "NO ROYALTY CUT",
  "AI MASTERED",
  "STRIPE PAID",
];
const WORDS_BOTTOM = [
  "MELO MONDAYS",
  "GREEN ROOM",
  "$2 A MONTH",
  "$0.99 A SONG",
  "$15 AN ALBUM",
  "100% YOURS",
];

function Row({ words, reverse = false, outline = false }) {
  const line = [...words, ...words, ...words];
  return (
    <div className="overflow-hidden">
      <motion.div
        className="flex whitespace-nowrap gap-8 md:gap-12"
        animate={{ x: reverse ? ["-33.333%", "0%"] : ["0%", "-33.333%"] }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
      >
        {line.map((w, i) => (
          <span
            key={`${w}-${i}`}
            className={`font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-none ${
              outline
                ? "text-transparent [-webkit-text-stroke:1px_#FF4500]"
                : "text-white"
            }`}
          >
            {w}
            <span className="text-[#FF4500] mx-4 md:mx-8">·</span>
          </span>
        ))}
      </motion.div>
    </div>
  );
}

export default function MarqueeBand() {
  return (
    <section data-testid="marquee-band" className="relative border-y border-[#2A2A2A] bg-black py-10 md:py-14 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 50%, rgba(255,69,0,0.2), transparent 70%)",
        }}
      />
      <div className="relative space-y-2 md:space-y-4">
        <Row words={WORDS_TOP} />
        <Row words={WORDS_BOTTOM} reverse outline />
      </div>
    </section>
  );
}

/**
 * FinalCTA — the closing "join the forge" card, ember-glow accented.
 */
export function FinalCTA() {
  return (
    <section
      data-testid="final-cta"
      className="relative bg-[#0A0A0A] border-b border-[#2A2A2A] overflow-hidden"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, rgba(255,69,0,0.25), transparent 70%)",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-4 inline-flex items-center gap-2">
            <Zap className="h-4 w-4" /> The forge is hot
          </div>
          <h2 className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] max-w-4xl mx-auto">
            Two dollars<br />
            <span className="text-[#FF4500]">a month.</span>{" "}
            <span className="text-gray-500">That's it.</span>
          </h2>
          <p className="font-mono text-sm md:text-base text-gray-300 mt-6 max-w-xl mx-auto leading-relaxed">
            Unlimited tracks. Free AI mastering. Free radio spins. Merch pipeline. Your own storefront.
            No royalty haircut, no gatekeepers.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/register" data-testid="final-cta-register">
              <button className="group bg-[#FF4500] text-black h-14 px-8 font-mono text-sm uppercase tracking-widest hover:bg-[#E53E00] transition-colors flex items-center gap-2 shadow-[0_20px_60px_-15px_rgba(255,69,0,1)]">
                Start selling
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
            <Link to="/manual" data-testid="final-cta-manual">
              <button className="border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] h-14 px-8 font-mono text-sm uppercase tracking-widest transition-colors bg-black/40 backdrop-blur">
                Read the manual
              </button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
